/**
 * Offers Drawer Component - JavaScript
 * Handles drawer open/close, copy functionality, and GSAP animations
 * ============================================
 * 
 * Usage:
 * 1. Add data-offers-toggle to any element to toggle the drawer
 * 2. Dispatch 'offers:open' or 'offers:close' events on window
 * 
 * Examples:
 *   <button data-offers-toggle>View Offers</button>
 *   window.dispatchEvent(new CustomEvent('offers:open'));
 *   window.dispatchEvent(new CustomEvent('offers:close'));
 */

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.__offersDrawerInitialized) return;
  window.__offersDrawerInitialized = true;

  document.addEventListener('DOMContentLoaded', initOffersDrawer);

  function initOffersDrawer() {
    const drawer = document.querySelector('[data-offers-drawer]');
    if (!drawer) return;

    const closeBtn = drawer.querySelector('[data-offers-close]');
    const overlay = drawer.querySelector('[data-offers-overlay]');
    const copyBtns = drawer.querySelectorAll('[data-copy-code]');
    
    // Track if cart drawer was open when offers opened
    let cartWasOpen = false;

    /**
     * Open the offers drawer
     */
    function openDrawer() {
      // Check if cart drawer is currently open
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      cartWasOpen = cartDrawer && cartDrawer.getAttribute('aria-hidden') === 'false';
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('offers-drawer-open');

      // Update all toggle buttons
      document.querySelectorAll('[data-offers-toggle]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'true');
      });

      // Focus on close button
      setTimeout(() => closeBtn.focus(), 100);

      // GSAP animation for offer items
      if (typeof gsap !== 'undefined') {
        const items = drawer.querySelectorAll('[data-offers-item]');
        
        gsap.fromTo(items,
          { opacity: 0, x: 30 },
          { 
            opacity: 1, 
            x: 0, 
            duration: 0.4, 
            stagger: 0.05,
            ease: 'power3.out',
            delay: 0.1
          }
        );
      }

      // Dispatch opened event
      window.dispatchEvent(new CustomEvent('offers:opened'));
    }

    /**
     * Close the offers drawer
     */
    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('offers-drawer-open');

      // Update all toggle buttons
      document.querySelectorAll('[data-offers-toggle]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'false');
      });

      // If cart drawer was open, focus its close button instead
      if (cartWasOpen) {
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        const cartCloseBtn = cartDrawer?.querySelector('[data-cart-close]');
        if (cartCloseBtn) {
          setTimeout(() => cartCloseBtn.focus(), 100);
        }
        cartWasOpen = false;
      } else {
        // Return focus to first toggle button
        const firstToggle = document.querySelectorAll('[data-offers-toggle]')[0];
        if (firstToggle) {
          firstToggle.focus();
        }
      }

      // Dispatch closed event
      window.dispatchEvent(new CustomEvent('offers:closed'));
    }

    /**
     * Toggle the drawer state
     */
    function toggleDrawer() {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    /**
     * Copy code to clipboard
     */
    async function copyCode(btn) {
      const code = btn.dataset.copyCode;
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        
        // Add copied state
        btn.classList.add('is-copied');
        
        // Remove copied state after 2 seconds
        setTimeout(() => {
          btn.classList.remove('is-copied');
        }, 2000);

        // Dispatch copy event
        window.dispatchEvent(new CustomEvent('offers:copied', { 
          detail: { code } 
        }));

      } catch (err) {
        console.error('Failed to copy code:', err);
        
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        
        try {
          document.execCommand('copy');
          btn.classList.add('is-copied');
          setTimeout(() => btn.classList.remove('is-copied'), 2000);
        } catch (e) {
          console.error('Fallback copy failed:', e);
        }
        
        document.body.removeChild(textArea);
      }
    }

    // ============================================
    // EVENT LISTENERS
    // ============================================

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    // Overlay click
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Copy buttons
    copyBtns.forEach(btn => {
      btn.addEventListener('click', () => copyCode(btn));
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    // Toggle buttons (using event delegation for dynamic elements)
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-offers-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        toggleDrawer();
      }
    });

    // Custom events for programmatic control
    window.addEventListener('offers:open', openDrawer);
    window.addEventListener('offers:close', closeDrawer);
    window.addEventListener('offers:toggle', toggleDrawer);

    // Re-initialize Lucide icons after drawer is added to DOM
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

})();

